// kid-carnival — Karnaval kid birthday card (b09).
//
// Playful but still reads as a printed invitation: a candy-striped outer
// ring frames a cream inner page with a round photo medallion ringed
// in carnival dots, plus a cheery balloon-ish ornament when no photo.
// "TUG'ILGAN KUN" eyebrow in roman small-caps, template font for the
// name, small-caps date + venue caption.
import { Photo, type LayoutCtx } from "./_shared";

export function KidCarnivalLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  // Candy-stripe diagonal pattern from the template accent.
  const STRIPE = `repeating-linear-gradient(45deg, ${accent} 0px, ${accent} 8px, ${bg} 8px, ${bg} 16px)`;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: STRIPE,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55, inset 0 0 0 5px ${bg}, inset 0 0 0 6px ${accent}55`,
        }}
      >
        {/* Confetti dots tucked behind the eyebrow */}
        {[
          { top: "8%", left: "12%", c: accent },
          { top: "10%", right: "16%", c: ink },
          { top: "14%", left: "32%", c: accent },
          { top: "12%", right: "32%", c: accent },
        ].map((d, i) => (
          <span
            key={i}
            style={{
              position: "absolute",
              width: 4,
              height: 4,
              borderRadius: "50%",
              background: d.c,
              opacity: 0.7,
              ...d,
            }}
            aria-hidden
          />
        ))}

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TUG&apos;ILGAN KUN
        </div>

        {/* Photo medallion with carnival-dot ring */}
        <div style={{ width: "60%", aspectRatio: "1 / 1", position: "relative", marginBottom: "5%" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              overflow: "hidden",
              boxShadow: `0 0 0 4px ${bg}, 0 0 0 5px ${accent}aa`,
            }}
          >
            <Photo
              src={photo}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
              fallback={
                <>
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: `radial-gradient(circle at 50% 45%, ${accent}44, ${bg})`,
                    }}
                  />
                  {/* Balloon trio */}
                  <svg viewBox="0 0 80 80" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} aria-hidden>
                    <ellipse cx="32" cy="36" rx="9" ry="11" fill={accent} opacity="0.85" />
                    <ellipse cx="48" cy="32" rx="9" ry="11" fill={ink} opacity="0.75" />
                    <ellipse cx="40" cy="48" rx="9" ry="11" fill={accent} opacity="0.6" />
                    <path d="M32 47 L34 64 M48 43 L46 64 M40 59 L40 70" stroke={ink} strokeWidth="0.6" opacity="0.55" />
                  </svg>
                </>
              }
            />
          </div>
          {/* Dot ring around medallion */}
          {Array.from({ length: 10 }).map((_, i) => {
            const a = (i / 10) * Math.PI * 2;
            const x = 50 + Math.cos(a) * 53;
            const y = 50 + Math.sin(a) * 53;
            return (
              <span
                key={i}
                style={{
                  position: "absolute",
                  left: `${x}%`,
                  top: `${y}%`,
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  background: i % 2 === 0 ? accent : ink,
                  transform: "translate(-50%, -50%)",
                  opacity: 0.85,
                }}
                aria-hidden
              />
            );
          })}
        </div>

        <div style={{ fontFamily: font, fontSize: 28, lineHeight: 1.1, fontWeight: 500, color: ink }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TUG&apos;ILGAN KUN · {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
